/**
 * Hook for the `ProjectSnapshot` widget.
 *
 * Owns the widget's static media references and derived copy so the render
 * file (`ProjectSnapshot.tsx`) stays declarative. No `useEffect`
 * (per the project-wide `no-use-effect` rule); media URLs are imported as
 * static module assets and the heading is derived synchronously from props.
 *
 * In the widget triplet, `useProjectSnapshot.ts` is the home for state,
 * handlers and derived data.
 */
import { useTranslation } from 'react-i18next';
import deliverVideo from '@/shared/assets/media/springboard/deliver.mp4';
import deliverFallback from '@/shared/assets/media/springboard/deliver.webp';
import type { ProjectSnapshotProps, ProjectSnapshotViewModel } from './ProjectSnapshot.types';

export function useProjectSnapshot(props: ProjectSnapshotProps): ProjectSnapshotViewModel {
  const { t } = useTranslation('projects');

  const { status, title, description, onCreateBrief } = props;

  // the snapshot card's big white headline is the project
  // description. When the project has no description yet (legacy projects
  // or freshly-created ones whose form left it empty) we fall back to the
  // onboarding line — better than rendering an empty heading.
  const trimmedDescription = description?.trim();
  const heading = trimmedDescription
    ? trimmedDescription
    : t('projectSnapshot.useProjectSnapshot.youJustStarted', { title });

  return {
    status,
    heading,
    ctaText: t('projectSnapshot.useProjectSnapshot.startByCreatingYourFirst'),
    ctaLabel: t('projectSnapshot.useProjectSnapshot.create'),
    videoSrc: deliverVideo,
    videoFallbackSrc: deliverFallback,
    onCreateBrief,
  };
}
